// ENG-5453: order-aware diff for vetting-query lists. A reorder leaves the set of
// items unchanged, so a membership diff reports "no items changed". This classifies
// each item of the before/after lists as stable / moved / added / removed, where
// "moved" is only the minimal set of items needed to explain the new order (via a
// longest-increasing-subsequence of the surviving items' new positions). Dragging
// one item therefore highlights just that item, not every row that shifted under it.

export type ReorderRowStatus = 'stable' | 'moved' | 'added' | 'removed';

export interface ReorderRow {
  label: string;
  status: ReorderRowStatus;
}

export interface ReorderDiff {
  before: ReorderRow[];
  after: ReorderRow[];
}

// Indices of one longest strictly-increasing subsequence of `seq` — the items that
// kept their relative order (the stable anchors).
function lisIndices(seq: number[]): Set<number> {
  const stable = new Set<number>();
  const n = seq.length;
  if (n === 0) return stable;

  const len = new Array<number>(n).fill(1);
  const prev = new Array<number>(n).fill(-1);
  let best = 0;
  for (let i = 0; i < n; i += 1) {
    for (let j = 0; j < i; j += 1) {
      if (seq[j] < seq[i] && len[j] + 1 > len[i]) {
        len[i] = len[j] + 1;
        prev[i] = j;
      }
    }
    if (len[i] > len[best]) best = i;
  }
  for (let k = best; k !== -1; k = prev[k]) stable.add(k);
  return stable;
}

// Disambiguate duplicate labels (e.g. multiple redacted "(hidden question)") by
// occurrence, so the k-th duplicate in `before` matches the k-th in `after`.
function occurrenceKeys(labels: string[]): string[] {
  const seen = new Map<string, number>();
  return labels.map((label) => {
    const n = seen.get(label) ?? 0;
    seen.set(label, n + 1);
    return `${label} ${n}`;
  });
}

export function diffOrderedItems(before: string[], after: string[]): ReorderDiff {
  const beforeKeys = occurrenceKeys(before);
  const afterKeys = occurrenceKeys(after);
  const afterIndex = new Map(afterKeys.map((key, i) => [key, i]));
  const beforeKeySet = new Set(beforeKeys);

  // Surviving items (present in both), in before order, with their after positions.
  const survivorBeforePos: number[] = [];
  const survivorAfterPos: number[] = [];
  beforeKeys.forEach((key, i) => {
    const afterPos = afterIndex.get(key);
    if (afterPos !== undefined) {
      survivorBeforePos.push(i);
      survivorAfterPos.push(afterPos);
    }
  });

  const anchors = lisIndices(survivorAfterPos);
  const stableBefore = new Set<number>();
  const stableAfter = new Set<number>();
  anchors.forEach((p) => {
    stableBefore.add(survivorBeforePos[p]);
    stableAfter.add(survivorAfterPos[p]);
  });

  const beforeRows: ReorderRow[] = before.map((label, i) => {
    let status: ReorderRowStatus = 'moved';
    if (!afterIndex.has(beforeKeys[i])) status = 'removed';
    else if (stableBefore.has(i)) status = 'stable';
    return { label, status };
  });

  const afterRows: ReorderRow[] = after.map((label, j) => {
    let status: ReorderRowStatus = 'moved';
    if (!beforeKeySet.has(afterKeys[j])) status = 'added';
    else if (stableAfter.has(j)) status = 'stable';
    return { label, status };
  });

  return { before: beforeRows, after: afterRows };
}
